import { SESSION_STORAGE_KEYS } from '@chegg-wt/core/utils/sessionStorage'
import { AppState, LogicDeps } from '@chegg-wt/core/state'
import { navigated, flowed } from '@chegg-wt/core/state/app/actions'
import { createLogic } from 'redux-logic'

import { FSACreatorPayload } from '@chegg-wt/core/state/types'
import { searchSources } from '@chegg-wt/core/api/source'
import {
  emitSuccessEvent,
  WORLDCAT_API_REQUEST,
} from '@chegg-wt/core/state/instrumentation/actions'
import {
  evalSourceTypes,
  hasWorldcatLink,
  isAutociteUrl,
  isWrongTypeError,
  getSourceType,
  getAutociteUrl,
  hasAutociteUrl,
} from '@chegg-wt/core/utils/searchSources'
import { sendPlusCiteRioEvent } from '@chegg-wt/core/api/plusCiteRioEvent'
import { getClientCommonFields } from '@chegg-wt/core/utils/plusCiteRioEvent'
import { softLoggedInSelector, jwtSelector } from '@chegg-wt/core/state/user/selectors'
import {
  journalEngineSelected,
  sourceSearchFailed,
  sourceSearchResultsPageLoaded,
  sourceSearchSucceeded,
  sourceSelected,
  sourceTypeSelected,
  sourceQueryEntered,
  sourceSelectionCompleted,
  sourceSelectionAutociteRequestFailed,
} from './actions'
import { ActionTypes } from './constants'
import {
  autociteApiVersionSelector,
  autociteV0RioOptFlagSelector,
  createCitationQuerySelector,
  createCitationSourceTypeSelector,
  sourceTypeManualSelector,
  searchApiVariantSelector,
} from './selectors'

export const sourceSearchLogic = createLogic<
  AppState,
  ReturnType<typeof sourceSearchResultsPageLoaded>['payload'],
  any,
  LogicDeps
>({
  type: ActionTypes.CREATE_CITATION_SEARCH_RESULTS_PAGE_LOADED,
  async process({ action, routes, getState }, dispatch, done) {
    const {
      sourceType,
      query: searchQuery,
      style,
      crossrefResultCountEnabled,
    } = action.payload
    try {
      const state = getState()
      const { cheggUserUuid } = jwtSelector(state) || {}
      const autociteApiVersion = autociteApiVersionSelector(state)
      const searchApiVariant = searchApiVariantSelector(state)
      const sources = await searchSources({
        sourceType,
        searchQuery,
        autociteApiVersion,
        clientCommonFields: {
          softLoggedIn: softLoggedInSelector(state),
          cheggUserUuid,
        },
        crossrefResultCountEnabled,
        searchApiVariant,
      })
      dispatch(sourceSearchSucceeded(sources))

      if (hasWorldcatLink(sourceType)) {
        dispatch(emitSuccessEvent({ viewName: WORLDCAT_API_REQUEST, value: sourceType }))
      }
    } catch (error) {
      dispatch(sourceSearchFailed(error))
      if (isAutociteUrl(sourceType, searchQuery) && isWrongTypeError(error)) {
        dispatch(navigated(routes.toManualCitation({ style, sourceType })))
      }
    } finally {
      done()
    }
  },
})

export const journalSearchLogic = createLogic<
  AppState,
  ReturnType<typeof journalEngineSelected>['payload'],
  any,
  LogicDeps
>({
  type: ActionTypes.CREATE_CITATION_JOURNAL_TAB_SELECTED,
  async process({ action, getState }) {
    const { type: journalType, crossrefResultCountEnabled } = action.payload
    const state = getState()
    const { cheggUserUuid } = jwtSelector(state) || {}
    const searchQuery = createCitationQuerySelector(state)
    const sourceType = createCitationSourceTypeSelector(state)
    const autociteApiVersion = autociteApiVersionSelector(state)
    const searchApiVariant = searchApiVariantSelector(state)

    try {
      const sources = await searchSources({
        searchQuery,
        sourceType,
        autociteApiVersion,
        clientCommonFields: {
          softLoggedIn: softLoggedInSelector(state),
          cheggUserUuid,
        },
        journalType,
        crossrefResultCountEnabled,
        searchApiVariant,
      })
      return sourceSearchSucceeded(sources)
    } catch (error) {
      return sourceSearchFailed(error)
    }
  },
})

export const sourceTypeSelectedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceTypeSelected>,
  any,
  LogicDeps
>({
  type: [sourceTypeSelected],
  validate({ action }, allow, reject) {
    const { redirectTo } = action.payload
    if (redirectTo) {
      allow(action)
    } else {
      reject(action)
    }
  },
  process({ action, getState }) {
    const state = getState()
    const manual = sourceTypeManualSelector(state)
    const { redirectTo } = action.payload

    if (manual) {
      return flowed('toManualCitation')
    }
    return flowed(redirectTo)
  },
})

export const sourceQueryEnteredLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceQueryEntered>,
  any,
  LogicDeps
>({
  type: [sourceQueryEntered],
  process({ getState }) {
    const manual = sourceTypeManualSelector(getState())
    if (manual) {
      return flowed('toManualCitation')
    }
    return flowed('toSourceSelection')
  },
})

export const sourceSelectedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceSelected>,
  any,
  LogicDeps
>({
  type: [sourceSelected],
  validate({ action }, allow, reject) {
    const { selectedSource, searchQuery } = action.payload
    if (hasAutociteUrl(selectedSource)) {
      allow(action)
    } else {
      reject(sourceSelectionCompleted(selectedSource, searchQuery))
    }
  },
  async process({ action, getState }, dispatch, done) {
    const { selectedSource, searchQuery } = action.payload
    const sourceType = getSourceType(selectedSource)
    const autociteUrl = getAutociteUrl(selectedSource)

    try {
      const state = getState()
      const { cheggUserUuid } = jwtSelector(state) || {}
      const autociteApiVersion = autociteApiVersionSelector(state)
      const searchApiVariant = searchApiVariantSelector(state)
      const sources = await searchSources({
        searchQuery: autociteUrl,
        sourceType,
        autociteApiVersion,
        clientCommonFields: {
          softLoggedIn: softLoggedInSelector(state),
          cheggUserUuid,
        },
        searchApiVariant,
      })
      // We expect only one result returned
      const sourcesLength = sources.length
      if (sourcesLength === 1) {
        dispatch(sourceSelectionCompleted(sources[0], searchQuery))
        done()
      }
      const incorrectSourceResultError = new Error(
        `Incorrect result count ${sourcesLength} returned, expect 1`
      )
      dispatch(sourceSelectionAutociteRequestFailed(incorrectSourceResultError))
    } catch (error) {
      dispatch(sourceSelectionAutociteRequestFailed(error))
    }

    dispatch(sourceSelectionCompleted(selectedSource, searchQuery))
    done()
  },
})

export const sourceSelectionCompletedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof sourceSelectionCompleted>,
  any,
  LogicDeps
>({
  type: [sourceSelectionCompleted],
  async process({ action, getState }) {
    const { selectedSource, searchQuery } = action.payload
    if (selectedSource.autociteRioTrackingID) {
      const { cheggUserUuid } = jwtSelector(getState()) || {}
      const state = getState()
      const autociteV0RioOptFlag = autociteV0RioOptFlagSelector(state)
      const autociteApiVersion = autociteApiVersionSelector(state)
      if (autociteApiVersion === 'v0' || autociteV0RioOptFlag) {
        sendPlusCiteRioEvent(
          selectedSource.autociteResponseForRio,
          selectedSource.numFieldsAutocite,
          selectedSource.autociteRioTrackingID,
          getClientCommonFields(softLoggedInSelector(getState()), cheggUserUuid)
        )
      }
    }
    const source = createCitationSourceTypeSelector(getState())
    const shouldSkipEvalPage = !evalSourceTypes.includes(source)
    const saveData = {
      ...selectedSource,
      searchQuery,
    }
    sessionStorage.setItem(SESSION_STORAGE_KEYS.searchData, JSON.stringify(saveData))
    return shouldSkipEvalPage ? flowed('toNewCitation') : flowed('toSourceConfirmation')
  },
})
